import { useState, useEffect, useCallback } from 'react';

import useGetMiceToTrapAmountChartData from './useGetMiceToTrapAmountChartData';

import { Period } from 'components/AnalyticsPage/Periods';
import { TrapType } from 'components/AnalyticsPage/TrapType';

interface ChartData {
  date: string[];
  mct_total_pests?: number[];
  mct_total_online_traps?: number[];
  smst_total_pests?: number[];
  smst_total_online_traps?: number[];
  srst_total_pests?: number[];
  srst_total_online_traps?: number[];
}

interface UseChartDataProps {
  slug: string;
  trapType: TrapType;
}

export const useGetMiceToTrapAmountChartState = ({ slug, trapType }: UseChartDataProps) => {
  const [period, setPeriod] = useState<Period>(Period.THREE_MONTH);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [showCustom, setShowCustom] = useState<boolean>(true);

  const { data, refetch, isLoading, isFetching } = useGetMiceToTrapAmountChartData(
    slug,
    period,
    trapType,
    startDate,
    endDate,
    false,
  );

  const [chartData, setChartData] = useState<ChartData>({
    date: [],
  });

  useEffect(() => {
    if (!data || isFetching) {
      setChartData({
        date: [],
        mct_total_pests: [],
        mct_total_online_traps: [],
        smst_total_pests: [],
        smst_total_online_traps: [],
        srst_total_pests: [],
        srst_total_online_traps: [],
      });
      return;
    }

    const date = data.map((item: any) => item.date);
    if (trapType === TrapType.SRST) {
      const srst_total_pests = data.map((item: any) => item.srst_total_pests);
      const srst_total_online_traps = data.map((item: any) => item.srst_total_online_traps);
      setChartData({
        date,
        srst_total_pests,
        srst_total_online_traps,
      });
    } else if (trapType === TrapType.SMST) {
      const smst_total_pests = data.map((item: any) => item.smst_total_pests);
      const smst_total_online_traps = data.map((item: any) => item.smst_total_online_traps);
      setChartData({
        date,
        smst_total_pests,
        smst_total_online_traps,
      });
    } else if (trapType === TrapType.MCT) {
      const mct_total_pests = data.map((item: any) => item.mct_total_pests);
      const mct_total_online_traps = data.map((item: any) => item.mct_total_online_traps);
      setChartData({
        date,
        mct_total_pests,
        mct_total_online_traps,
      });
    } else {
      const mct_total_pests = data.map((item: any) => item.mct_total_pests);
      const mct_total_online_traps = data.map((item: any) => item.mct_total_online_traps);
      const smst_total_pests = data.map((item: any) => item.smst_total_pests);
      const smst_total_online_traps = data.map((item: any) => item.smst_total_online_traps);

      setChartData({
        date,
        mct_total_pests,
        mct_total_online_traps,
        smst_total_pests,
        smst_total_online_traps,
      });
    }
  }, [data, trapType, isFetching]);

  useEffect(() => {
    const currentPath = window.location.pathname;

    if (!currentPath.includes('analitics')) {
      return;
    }

    if (startDate && endDate) {
      const timer = setTimeout(() => {
        refetch();
      }, 1000);

      return () => clearTimeout(timer);
    } else if (period !== 'CUSTOM') {
      refetch();
    }
  }, [slug, trapType, startDate, endDate, period, refetch]);

  const handlePeriodChange = useCallback((newPeriod: Period) => {
    if (newPeriod === 'CUSTOM') {
      const today = new Date();
      const oneYearBefore = new Date();
      oneYearBefore.setFullYear(today.getFullYear() - 1);

      setStartDate(oneYearBefore.toISOString().split('T')[0]);
      setEndDate(today.toISOString().split('T')[0]);
      setShowCustom(false);
    } else {
      setStartDate('');
      setEndDate('');
      setShowCustom(true);
    }
    setPeriod(newPeriod);
  }, []);

  return {
    period,
    startDate,
    endDate,
    showCustom,
    chartData,
    isLoading,
    isFetching,
    handlePeriodChange,
    setStartDate,
    setEndDate,
    refetch,
  };
};
